/**
 * @architecture-header 1
 * @layer shared
 * @domain accessibility
 * @responsibility Give clickable rows and cards keyboard support without changing their markup.
 * @state-owner none
 * @allowed-imports React types
 * @forbidden business rules; network; store access
 * @public-api workspace shell and modules
 * @side-effects none
 * @tests workspace-behaviour.test.ts
 */

import type { KeyboardEvent, MouseEvent } from 'react';

export interface ClickableProps<T extends HTMLElement> {
  readonly onClick: (event: MouseEvent<T>) => void;
  readonly onKeyDown: (event: KeyboardEvent<T>) => void;
}

/**
 * The source design uses styled `div`/`span` rows as click targets. Keeping those
 * elements preserves the visual language, so they pair this with an explicit
 * `role="button"` and `tabIndex={0}` at each call site.
 */
export function clickable<T extends HTMLElement>(
  onSelect: (event: MouseEvent<T> | KeyboardEvent<T>) => void,
): ClickableProps<T> {
  return {
    onClick: onSelect,
    onKeyDown: (event: KeyboardEvent<T>) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      onSelect(event);
    },
  };
}
